import React, {FC, useEffect, useMemo, useState} from 'react';
import './edit-connection-modal.scss';
import GenericModal from '../../../../shared/generic-modal/generic-modal';
import { CustomInput } from 'src/components/shared/custom-input/custom-input';
import {
    DUPLICATE_SERVICE_NAME,
    ILLEGAL_CHARACTERS_MESSAGE,
    isValueDuplicate,
    validateString
} from '../../../../../utils/validationUtils';
import FormField from '../../../../shared/edit-row-modal/form-field/form-field';
import { ConnectionModel } from '../../connection.model';
import { useStores } from '../../../../store';
import { observer } from 'mobx-react-lite';
import PolicyStatusLabel from '../../../../shared/policy-status-display/policy-status-label';
import usePromiseFeedback from '../../../../shared/promise-feedback/usePromiseFeedback';
import { EventType } from '../../../../../dtos/analytics.dto';


interface EditConnectionModalProps {
    visible: boolean;
    onClose: () => void;
    connection: ConnectionModel;
}

interface EditFields {
    connectionName: string;
    policyName: string;
    policyComments: string;
}

const getInitialFields = (connection: ConnectionModel): EditFields => ({
    connectionName: (connection.connectionName || connection.circuitId) ?? '',
    policyName: connection.policyName ?? '',
    policyComments: connection.policyVersionComments ?? ''
});

const EditConnectionModal: FC<EditConnectionModalProps> = observer(({ visible, onClose, connection }) => {
    const [connectionDetails, setConnectionDetails] = useState<EditFields>(getInitialFields(connection));
    const [promiseFeedbackFactory] = usePromiseFeedback();
    const [isLoading, setIsLoading] = useState(false);
    const { customerStore } = useStores();

    useEffect(() => {
        setConnectionDetails(getInitialFields(connection));
    }, [connection.policyExternalId]);

    const generateError = (value: string) => {
        return !value || !validateString(value, false, ['@', '#', '$', '%', '&', '*', '+', '/']) ? '' : ILLEGAL_CHARACTERS_MESSAGE;
    };

    const nameError = useMemo(() => {
        let err = generateError(connectionDetails.connectionName);
        if(!err) {
            const listToCompare = customerStore.selectedCustomer?.services;
            if (isValueDuplicate(connectionDetails.connectionName, connection.connectionName, listToCompare, 'serviceName') ||
                isValueDuplicate(connectionDetails.connectionName, connection.circuitId, listToCompare, 'adiCircuitId')) {

                err = DUPLICATE_SERVICE_NAME;
            }
        }
        return err;
    }, [connectionDetails.connectionName]);

    const policyNameError = generateError(connectionDetails.policyName);
    const commentsError = generateError(connectionDetails.policyComments);
    const hasError = !!nameError || !!policyNameError || !!commentsError;

    const handleCancel = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'cancel', location: 'editConnectionDetailsModal' });
        onClose();
    };

    const handleConfirm = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'save', location: 'editConnectionDetailsModal' });
        setIsLoading(true);
        promiseFeedbackFactory({
            promise: customerStore.setConnectionDetails(connection.id,
                connection.policyExternalId,
                connectionDetails.connectionName,
                connectionDetails.policyName,
                connectionDetails.policyComments),
            notifObj: {show: true, notificationMsg: 'Details updated successfully'},
            errorCallback: () => setIsLoading(false),
            successCallback: () => {
                setIsLoading(false);
                onClose();
            }
        });
    };

    const isDirty =
        connectionDetails.connectionName !== connection.connectionName ||
        connectionDetails.policyName !== connection.policyName ||
        connectionDetails.policyComments !== connection.policyVersionComments;

    return (
        <GenericModal
            open={visible}
            title="Edit Connection Details"
            wrapClassName="edit-connection-details-modal"
            closable={false}
            width={562}
            body={
                <div className="modal-body">
                    {!!connection.policyStatus &&
                        <div className="version-status-wrapper"><PolicyStatusLabel label={connection.policyStatus}
                                                                                   versionNumber={connection.policyVersionNumber}/>
                        </div>}
                    <FormField label="Connection Name" errorMessage={nameError}>
                        <CustomInput
                            inputType="INPUT"
                            className="connection-name"
                            hideWarnMessage={!!nameError}
                            value={connectionDetails.connectionName}
                            placeholder="Enter connection name"
                            onChange={(e) => setConnectionDetails(f => ({ ...f, connectionName: e.target.value }))}
                            maxLength={25}
                        />
                    </FormField>
                    <FormField label="Policy Name" errorMessage={policyNameError}>
                        <CustomInput
                            inputType="INPUT"
                            className="policy-name"
                            hideWarnMessage={!!policyNameError}
                            value={connectionDetails.policyName}
                            placeholder="Enter policy name"
                            onChange={(e) => setConnectionDetails(f => ({ ...f, policyName: e.target.value }))}
                            maxLength={50}
                        />
                    </FormField>
                    <FormField label="Policy Comments" errorMessage={commentsError}>
                        <CustomInput
                            inputType="TEXTAREA"
                            className="policy-comments"
                            hideWarnMessage={!!commentsError}
                            value={connectionDetails.policyComments}
                            placeholder="Enter policy comments"
                            onChange={(e) => setConnectionDetails(f => ({ ...f, policyComments: e.target.value }))}
                            maxLength={250}
                            rows={3}
                        />
                    </FormField>
                </div>
            }
            actionButtons={[
                {
                    text: 'Cancel',
                    onClick: handleCancel,
                    className: 'secondary-btn',
                },
                {
                    text: 'Save',
                    onClick: () => handleConfirm(),
                    disabled: !isDirty || hasError,
                    className: `primary-btn ${isLoading ? 'loading' : ''}`
                },
            ]}
            onCancel={onClose}
        />
    );
});

export default EditConnectionModal;

